import { AnalyticsService } from '../analytics';
import { ConfigService } from '../config';
import { LoggerService, getLogger } from '../logging';
import { Container } from './container';

/** Options for container retrieval behavior. */
export interface ContainerOptions {
    /** Whether to return the cached container or create a fresh one. Default: true. */
    useCache?: boolean;
}

let cached: Container | null = null;

/**
 * Register core-level service factories in the IoC container.
 * All registrations are lazy; nothing is constructed until first `resolve()`.
 *
 * @returns a new container with core service factories registered
 */
function createContainer(): Container {
    const container = new Container();
    container.register(AnalyticsService, [ConfigService]);
    // One root logger shared by containerless callers (mobile, bootstrap) and DI consumers.
    container.instance(LoggerService, getLogger());

    return container;
}

/**
 * Get the core IoC container, creating it on first call and caching for subsequent calls.
 * Registers core-level service factories (currently just {@link AnalyticsService}).
 * Dependencies (e.g., {@link ConfigService}) must be registered as instances by the app
 * layer before anything is resolved.
 *
 * @param options - container retrieval options
 * @returns the core container with core service factories registered
 */
export function getContainer(options: ContainerOptions = {}): Container {
    const { useCache = true } = options;

    if (useCache && cached) {
        return cached;
    }

    const container = createContainer();
    cached = container;
    return container;
}
